import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getProfileDetails, updateProfileData } from '../../apis/api.egg'
import { useUIContext } from '../../context/UIContext'
import { Error } from '../../interfaces/error'
import { useTeam } from '../../pages/Team/Context/TeamContext'

export const useProfileDetails = (profileId: string, isAssigned?: boolean) => {
    const queryClient = useQueryClient()
    const { toastNotification } = useUIContext()
    const { unassigned, courseID, unassignedPage } = useTeam()
    const profileKey = ['getProfileDetails', profileId]

    const { data: profileDetails, isLoading } = useQuery(
        profileKey,
        () => getProfileDetails(`${profileId}`),
        {
            enabled: !!profileId,
            onError: (error: Error['response']) =>
                toastNotification(error.data.message, 'error', 5),
        }
    )

    const { mutate: mutateProfile } = useMutation(
        (params: Parameters<typeof updateProfileData>) => updateProfileData(...params),
        {
            onMutate: ([, data]) => {
                if (!isAssigned) {
                    // Remove from unassigned list
                    const key = [courseID, 'Team', 'Unassigned', unassignedPage]
                    const oldUnassigned = queryClient.getQueryData<typeof unassigned>(key)
                    queryClient.setQueryData(key, {
                        ...oldUnassigned,
                        results:
                            !!oldUnassigned?.results?.length &&
                            oldUnassigned?.results.filter(
                                (student) => student._profileId !== profileId
                            ),
                    })
                }

                const updatedAt = new Date()
                const snapshot = queryClient.getQueryData<typeof data>(profileKey)
                snapshot &&
                    queryClient.setQueryData(profileKey, { ...snapshot, ...data, updatedAt })
                return snapshot
            },
            onError: (error: Error['response'], _, snapshot) => {
                toastNotification(error.data.message, 'error', 5)
                snapshot && queryClient.setQueryData(profileKey, snapshot)
            },
        }
    )
    return { profileDetails, isLoading, mutateProfile }
}
